import { initBrazeAfterEngagement, hasMarketingCookieConsent } from './braze';

// ─────────────────────────────────────────────
// Service Worker Registration
// ─────────────────────────────────────────────

/**
 * Registers the Braze service worker for push notifications.
 * Should be called once on page load.
 */
export const registerBrazeServiceWorker = () => {
  if (!('serviceWorker' in navigator)) {
    // console.warn('[freedome][braze-push] Service workers not supported in this browser');
    return;
  }

  navigator.serviceWorker
    .register('/service-worker.js')
    .then((registration) => {
      // console.log('[freedome][braze-push] Service worker registered:', registration.scope);
    })
    .catch((error) => {
      // console.error('[freedome][braze-push] Service worker registration failed:', error);
    });
};

// ─────────────────────────────────────────────
// Push Permission
// ─────────────────────────────────────────────

/**
 * Returns the current push notification permission state.
 * @returns {'granted'|'denied'|'default'|'unsupported'}
 */
export const getPushPermissionState = () => {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
};

/**
 * Returns true if the browser supports web push notifications.
 */
export const isPushSupported = () =>
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

/**
 * Requests push notification permission from the user via Braze SDK.
 *
 * This triggers the native browser permission prompt.
 * Call this in response to a user action (e.g. button click) for best UX
 * and to avoid the browser blocking the prompt.
 *
 * @param {Object} [options]
 * @param {Function} [options.onGranted] - Callback when permission is granted
 * @param {Function} [options.onDenied]  - Callback when permission is denied
 * @param {Function} [options.onDismissed] - Callback when prompt is dismissed
 */
export const requestPushPermission = (options = {}) => {
  if (!isPushSupported()) {
    // console.warn('[freedome][braze-push] Push notifications not supported');
    options.onDenied?.();
    return;
  }

  // A registered push token is "Marketing" data (push_tokens, https://app.clickup.com/t/869dg1yhr).
  // Without Iubenda Marketing-cookie consent we don't request permission or
  // register a token, so push can't be enabled — report it as denied.
  // (push_subscribe, set via setBrazePushSubscription, stays essential.)
  if (!hasMarketingCookieConsent()) {
    options.onDenied?.();
    return;
  }

  initBrazeAfterEngagement(() => {
    window.braze.requestPushPermission(
      // success callback (permission granted)
      () => {
        // console.log('[freedome][braze-push] Push permission granted');
        options.onGranted?.();
      },
      // denied callback
      () => {
        // console.log('[freedome][braze-push] Push permission denied');
        options.onDenied?.();
      },
      // dismissed callback (user closed the prompt without choosing)
      // Note: not all browsers distinguish dismissed from denied
    );
  });
};

// ─────────────────────────────────────────────
// Push Subscription State
// ─────────────────────────────────────────────

/**
 * Sets the Braze push subscription state for the current user.
 * Call after the user opts in or out of push notifications in your UI.
 *
 * @param {boolean} isSubscribed
 */
export const setBrazePushSubscription = (isSubscribed) => {
  initBrazeAfterEngagement(() => {
    const brazeUser = window.braze.getUser();
    if (isSubscribed) {
      brazeUser.setPushNotificationSubscriptionType(
        window.braze.User.NotificationSubscriptionTypes.OPTED_IN
      );
    } else {
      brazeUser.setPushNotificationSubscriptionType(
        window.braze.User.NotificationSubscriptionTypes.UNSUBSCRIBED
      );
    }
    // console.log(
    //   `[freedome][braze-push] push_subscribe set to ${isSubscribed ? 'opted_in' : 'unsubscribed'}`
    // );
  });
};
